import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  UploadCloud, PenLine, CheckCircle2, Download, ExternalLink, Loader2, FileText,
} from "lucide-react";
import api, { clearToken, getToken, formatApiError } from "@/lib/api";
import { PortalHeader } from "@/pages/AdminDashboard";
import { Field, inputProps } from "@/components/Field";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const downloadFile = async (fileId, filename) => {
  try {
    const resp = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(formatApiError(e));
  }
};

const EMERGENCY_FIELDS = [
  { key: "contact_name", label: "Emergency contact name" },
  { key: "relationship", label: "Relationship" },
  { key: "phone", label: "Phone number" },
  { key: "alt_phone", label: "Alternate phone (optional)", required: false },
];

const DEPOSIT_FIELDS = [
  { key: "bank_name", label: "Bank name" },
  { key: "routing_number", label: "Routing number" },
  { key: "account_number", label: "Account number" },
  { key: "account_type", label: "Account type", type: "select", options: ["Checking", "Savings"] },
];

const I9_STATUSES = [
  "A citizen of the United States",
  "A noncitizen national of the United States",
  "A lawful permanent resident",
  "A noncitizen authorized to work",
];

const TB_SYMPTOMS = [
  ["productive_cough", "A productive cough"], ["night_sweats", "Night sweats"],
  ["fever", "Fever"], ["weight_loss", "Weight loss"], ["rash", "Rash"], ["illness", "An illness"],
];

const YesNo = ({ value, onChange, testid }) => (
  <div className="flex gap-2">
    {["Yes", "No"].map((o) => (
      <button type="button" key={o} data-testid={`${testid}-${o.toLowerCase()}`} onClick={() => onChange(o)}
        className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors ${
          value === o ? "border-gold bg-gold-soft text-gold-deep" : "border-blush-300/70 bg-white text-charcoal-muted hover:border-blush-400"}`}>
        {o}
      </button>
    ))}
  </div>
);

const OnboardingPortal = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [state, setState] = useState(null);
  const [signItem, setSignItem] = useState(null);
  const [signName, setSignName] = useState("");
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState(0);
  const [forms, setForms] = useState({ emergency_contact: {}, direct_deposit: {}, i9: {}, tb: {} });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/me");
      setUser(data.user);
      setState(data);
    } catch (e) {
      clearToken();
      navigate("/onboarding");
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) return navigate("/onboarding");
    load();
  }, [navigate, load]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearToken();
    navigate("/onboarding");
  };

  const upload = async (key, file) => {
    if (!file) return;
    setBusy(key);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/onboarding/items/${key}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => setProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0),
      });
      toast.success("Document uploaded securely.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const sign = async () => {
    if (signName.trim().length < 3) return toast.error("Please type your full legal name.");
    setBusy(signItem.key);
    try {
      await api.post(`/onboarding/items/${signItem.key}/sign`, { signature_name: signName.trim() });
      toast.success("Acknowledgment signed.");
      setSignItem(null);
      setSignName("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const submitForm = async (key, data) => {
    setBusy(key);
    try {
      await api.post(`/onboarding/items/${key}/form`, { data });
      toast.success("Saved securely.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!state || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-blush-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blush-300 border-t-gold" />
      </div>
    );
  }

  const pct = state.required_total ? Math.round((state.required_done / state.required_total) * 100) : 0;
  const itemState = (key) => state.checklist.find((c) => c.key === key);

  const renderActions = (item) => {
    if (item.complete) {
      return (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 rounded-full bg-emerald-50 px-3.5 py-1.5 text-xs font-semibold text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Completed {item.completed_at ? new Date(item.completed_at).toLocaleDateString("en-US") : ""}
          </span>
          {item.file && (
            <button onClick={() => downloadFile(item.file.file_id, item.file.original_filename)}
              className="flex items-center gap-1.5 text-xs font-semibold text-gold-deep hover:underline">
              <Download className="h-3.5 w-3.5" /> {item.file.original_filename}
            </button>
          )}
          {item.type !== "i9" && item.type !== "form" && (
            <label className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-charcoal-faint hover:text-gold-deep">
              Replace
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={(e) => upload(item.key, e.target.files[0])} />
            </label>
          )}
        </div>
      );
    }
    if (item.type === "upload") {
      return (
        <div className="mt-4">
          {busy === item.key ? (
            <div className="h-1.5 overflow-hidden rounded-full bg-blush-200">
              <div className="h-full bg-gold transition-[width]" style={{ width: `${progress}%` }} />
            </div>
          ) : (
            <label data-testid={`onboarding-upload-${item.key}`}
              className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blush-300 bg-white px-4 py-5 text-sm text-charcoal-muted transition-colors hover:border-gold/50 hover:bg-blush-50">
              <UploadCloud className="h-5 w-5 text-gold" strokeWidth={1.5} /> Upload document (PDF, JPG, PNG, DOC — 10 MB max)
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={(e) => upload(item.key, e.target.files[0])} />
            </label>
          )}
        </div>
      );
    }
    if (item.type === "sign") {
      return (
        <button onClick={() => setSignItem(item)} data-testid={`onboarding-sign-${item.key}`} className="btn-pink mt-4 !px-5 !py-2.5 !text-xs">
          <PenLine className="h-3.5 w-3.5" /> Review & Sign
        </button>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-blush-50/50">
      <PortalHeader title="Onboarding Portal" onLogout={logout} />
      <main className="mx-auto max-w-4xl px-5 py-10 sm:px-8">
        <h1 className="font-serif text-3xl text-charcoal" data-testid="onboarding-welcome">Welcome, {user.name || "new team member"}</h1>
        <p className="mt-2 text-sm leading-relaxed text-charcoal-muted">
          Complete each item below. Everything you submit is encrypted, stored privately, and visible only to
          authorized New Beginnings administrators — never sent by email.
        </p>

        <div className="mt-6 rounded-2xl border border-blush-200 bg-white p-5" data-testid="onboarding-progress">
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.14em] text-charcoal-muted">
            <span>Checklist progress</span>
            <span className="text-gold-deep">{state.required_done} of {state.required_total} required items</span>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-blush-200">
            <div className="h-full rounded-full bg-gradient-to-r from-rosebtn to-gold transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="mt-8 grid gap-5">
          {state.checklist.map((item) => (
            <div key={item.key} data-testid={`onboarding-item-${item.key}`}
              className={`rounded-2xl border bg-white p-6 ${item.complete ? "border-emerald-200" : "border-blush-200"}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-serif text-lg text-charcoal">
                    {item.title}
                    {!item.required && <span className="ml-2 text-xs font-sans font-medium uppercase tracking-wide text-charcoal-faint">Optional</span>}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-charcoal-muted">{item.description}</p>
                  {item.link && (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" data-testid={`onboarding-link-${item.key}`}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-gold-deep hover:underline">
                      <ExternalLink className="h-3.5 w-3.5" /> {item.link_label}
                    </a>
                  )}
                </div>
                {item.complete && <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-600" />}
              </div>

              {item.type === "form" && item.key === "emergency_contact" && !item.complete && (
                <form onSubmit={(e) => { e.preventDefault(); submitForm("emergency_contact", forms.emergency_contact); }}
                  className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="emergency-contact-form">
                  {EMERGENCY_FIELDS.map((f) => (
                    <Field key={f.key} label={f.label} htmlFor={`ec-${f.key}`} required={f.required !== false}>
                      <input id={`ec-${f.key}`} required={f.required !== false} value={forms.emergency_contact[f.key] || ""}
                        onChange={(e) => setForms({ ...forms, emergency_contact: { ...forms.emergency_contact, [f.key]: e.target.value } })}
                        {...inputProps(`emergency-${f.key.replace(/_/g, "-")}-input`)} />
                    </Field>
                  ))}
                  <button type="submit" disabled={busy === "emergency_contact"} data-testid="emergency-contact-submit" className="btn-pink !py-2.5 !text-xs disabled:opacity-60 sm:col-span-2">
                    {busy === "emergency_contact" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Emergency Contact"}
                  </button>
                </form>
              )}

              {item.type === "form" && item.key === "direct_deposit" && !item.complete && (
                <form onSubmit={(e) => { e.preventDefault(); submitForm("direct_deposit", forms.direct_deposit); }}
                  className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="direct-deposit-form">
                  <div className="rounded-xl border border-gold/30 bg-gold-soft/50 p-3 text-xs text-charcoal-muted sm:col-span-2">
                    Bank details are encrypted before storage and are only visible to payroll administrators.
                  </div>
                  {DEPOSIT_FIELDS.map((f) => (
                    <Field key={f.key} label={f.label} htmlFor={`dd-${f.key}`} required>
                      {f.type === "select" ? (
                        <select id={`dd-${f.key}`} required value={forms.direct_deposit[f.key] || ""}
                          onChange={(e) => setForms({ ...forms, direct_deposit: { ...forms.direct_deposit, [f.key]: e.target.value } })}
                          {...inputProps(`deposit-${f.key.replace(/_/g, "-")}-select`)}>
                          <option value="">Select</option>
                          {f.options.map((o) => <option key={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input id={`dd-${f.key}`} required value={forms.direct_deposit[f.key] || ""}
                          onChange={(e) => setForms({ ...forms, direct_deposit: { ...forms.direct_deposit, [f.key]: e.target.value } })}
                          {...inputProps(`deposit-${f.key.replace(/_/g, "-")}-input`)} />
                      )}
                    </Field>
                  ))}
                  <Field label="Signature (type full legal name)" htmlFor="dd-sig" required>
                    <input id="dd-sig" required className="field-input font-serif !text-lg italic" data-testid="deposit-signature-input"
                      value={forms.direct_deposit.signature_name || ""}
                      onChange={(e) => setForms({ ...forms, direct_deposit: { ...forms.direct_deposit, signature_name: e.target.value } })} />
                  </Field>
                  <button type="submit" disabled={busy === "direct_deposit"} data-testid="direct-deposit-submit" className="btn-pink !py-2.5 !text-xs disabled:opacity-60 sm:col-span-2">
                    {busy === "direct_deposit" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Direct Deposit"}
                  </button>
                </form>
              )}

              {item.type === "i9" && !item.complete && (
                <div className="mt-5 grid gap-5">
                  <form onSubmit={(e) => { e.preventDefault(); submitForm("i9_section1", forms.i9); }}
                    className="grid gap-4 rounded-2xl border border-blush-200 bg-blush-50/50 p-5" data-testid="i9-section1-form">
                    <p className="text-sm font-semibold text-charcoal">Section 1 — Employee Attestation</p>
                    <span className="field-label">I attest, under penalty of perjury, that I am:</span>
                    <div className="grid gap-2">
                      {I9_STATUSES.map((s) => (
                        <label key={s} className="flex cursor-pointer items-center gap-3 rounded-xl border border-blush-200 bg-white px-4 py-3 text-sm text-charcoal-light hover:border-gold/40">
                          <input type="radio" name="i9status" className="h-4 w-4 accent-[#C59B27]"
                            checked={forms.i9.citizenship_status === s}
                            onChange={() => setForms({ ...forms, i9: { ...forms.i9, citizenship_status: s } })}
                            data-testid={`i9-status-${s.split(" ").slice(-1)[0].replace(/[()]/g, "")}`} />
                          {s}
                        </label>
                      ))}
                    </div>
                    <span className="field-label">For in-person verification, I will present:</span>
                    <div className="grid gap-2">
                      {["One document from List A", "One document from List B AND one from List C"].map((s) => (
                        <label key={s} className="flex cursor-pointer items-center gap-3 rounded-xl border border-blush-200 bg-white px-4 py-3 text-sm text-charcoal-light hover:border-gold/40">
                          <input type="radio" name="i9plan" className="h-4 w-4 accent-[#C59B27]"
                            checked={forms.i9.document_plan === s}
                            onChange={() => setForms({ ...forms, i9: { ...forms.i9, document_plan: s } })} />
                          {s}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs leading-relaxed text-charcoal-faint">
                      The choice of documents is entirely yours — see the Lists of Acceptable Documents on the official
                      Form I-9. Do not upload photos of identity documents here; bring originals on your first day.
                    </p>
                    <Field label="Electronic signature (type full legal name)" htmlFor="i9-sig1" required>
                      <input id="i9-sig1" required className="field-input font-serif !text-lg italic" data-testid="i9-section1-signature-input"
                        value={forms.i9.signature_name || ""}
                        onChange={(e) => setForms({ ...forms, i9: { ...forms.i9, signature_name: e.target.value } })} />
                    </Field>
                    <button type="submit" disabled={busy === "i9_section1"} data-testid="i9-section1-submit" className="btn-pink !py-2.5 !text-xs disabled:opacity-60">
                      {busy === "i9_section1" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Section 1 Attestation"}
                    </button>
                  </form>
                  {renderActions({ ...item, type: "upload", complete: false })}
                </div>
              )}

              {item.type === "form" && item.key === "ppd_tb" && !item.complete && (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const tb = forms.tb;
                  submitForm("ppd_tb", { ...tb, symptoms: (tb.symptoms || []).join(", ") });
                }} className="mt-5 grid gap-4 rounded-2xl border border-blush-200 bg-blush-50/50 p-5" data-testid="tb-questionnaire-form">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="When was your last PPD?" htmlFor="tb-last">
                      <input id="tb-last" type="date" value={forms.tb.last_ppd || ""} onChange={(e) => setForms({ ...forms, tb: { ...forms.tb, last_ppd: e.target.value } })} {...inputProps("tb-last-ppd-input")} />
                    </Field>
                    <Field label="Date of last chest X-ray" htmlFor="tb-xray">
                      <input id="tb-xray" type="date" value={forms.tb.chest_xray_date || ""} onChange={(e) => setForms({ ...forms, tb: { ...forms.tb, chest_xray_date: e.target.value } })} {...inputProps("tb-xray-date-input")} />
                    </Field>
                  </div>
                  <div className="grid gap-2">
                    <span className="field-label">Have you had a positive PPD test?</span>
                    <YesNo value={forms.tb.positive_ppd || ""} onChange={(v) => setForms({ ...forms, tb: { ...forms.tb, positive_ppd: v } })} testid="tb-positive-ppd" />
                  </div>
                  {forms.tb.positive_ppd === "Yes" && (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="If so, when?" htmlFor="tb-when">
                        <input id="tb-when" value={forms.tb.positive_when || ""} onChange={(e) => setForms({ ...forms, tb: { ...forms.tb, positive_when: e.target.value } })} {...inputProps("tb-positive-when-input")} />
                      </Field>
                      <div>
                        <span className="field-label">Treated?</span>
                        <YesNo value={forms.tb.treated || ""} onChange={(v) => setForms({ ...forms, tb: { ...forms.tb, treated: v } })} testid="tb-treated" />
                      </div>
                    </div>
                  )}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="1st Step PPD — placed" htmlFor="tb-s1p">
                      <input id="tb-s1p" type="date" value={forms.tb.step1_placed || ""} onChange={(e) => setForms({ ...forms, tb: { ...forms.tb, step1_placed: e.target.value } })} {...inputProps("tb-step1-placed-input")} />
                    </Field>
                    <Field label="1st Step PPD — read" htmlFor="tb-s1r">
                      <input id="tb-s1r" type="date" value={forms.tb.step1_read || ""} onChange={(e) => setForms({ ...forms, tb: { ...forms.tb, step1_read: e.target.value } })} {...inputProps("tb-step1-read-input")} />
                    </Field>
                    <Field label="2nd Step PPD — placed" htmlFor="tb-s2p">
                      <input id="tb-s2p" type="date" value={forms.tb.step2_placed || ""} onChange={(e) => setForms({ ...forms, tb: { ...forms.tb, step2_placed: e.target.value } })} {...inputProps("tb-step2-placed-input")} />
                    </Field>
                    <Field label="2nd Step PPD — read" htmlFor="tb-s2r">
                      <input id="tb-s2r" type="date" value={forms.tb.step2_read || ""} onChange={(e) => setForms({ ...forms, tb: { ...forms.tb, step2_read: e.target.value } })} {...inputProps("tb-step2-read-input")} />
                    </Field>
                  </div>
                  <div>
                    <span className="field-label">Do you now have, or have you recently had, any of the following?</span>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {TB_SYMPTOMS.map(([k, label]) => {
                        const active = (forms.tb.symptoms || []).includes(label);
                        return (
                          <button type="button" key={k} data-testid={`tb-symptom-${k}`}
                            onClick={() => {
                              const cur = forms.tb.symptoms || [];
                              setForms({ ...forms, tb: { ...forms.tb, symptoms: active ? cur.filter((s) => s !== label) : [...cur, label] } });
                            }}
                            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                              active ? "border-gold bg-gold-soft text-gold-deep" : "border-blush-300/70 bg-white text-charcoal-muted hover:border-blush-400"}`}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <span className="field-label">Had a viral infection (like chicken pox) in the last 4 weeks?</span>
                      <YesNo value={forms.tb.viral_infection_4w || ""} onChange={(v) => setForms({ ...forms, tb: { ...forms.tb, viral_infection_4w: v } })} testid="tb-viral" />
                    </div>
                    <div>
                      <span className="field-label">Have you ever had a BCG vaccine?</span>
                      <YesNo value={forms.tb.bcg_vaccine || ""} onChange={(v) => setForms({ ...forms, tb: { ...forms.tb, bcg_vaccine: v } })} testid="tb-bcg" />
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Date of your last physical exam" htmlFor="tb-physical">
                      <input id="tb-physical" type="date" value={forms.tb.last_physical_date || ""} onChange={(e) => setForms({ ...forms, tb: { ...forms.tb, last_physical_date: e.target.value } })} {...inputProps("tb-last-physical-input")} />
                    </Field>
                    <Field label="Physician name" htmlFor="tb-physician">
                      <input id="tb-physician" value={forms.tb.physician_name || ""} onChange={(e) => setForms({ ...forms, tb: { ...forms.tb, physician_name: e.target.value } })} {...inputProps("tb-physician-name-input")} />
                    </Field>
                  </div>
                  <button type="submit" disabled={busy === "ppd_tb"} data-testid="tb-questionnaire-submit" className="btn-pink !py-2.5 !text-xs disabled:opacity-60">
                    {busy === "ppd_tb" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit TB Questionnaire"}
                  </button>
                </form>
              )}

              {item.allow_upload && !item.complete && (
                <div className="mt-4">
                  {busy === item.key ? (
                    <div className="h-1.5 overflow-hidden rounded-full bg-blush-200">
                      <div className="h-full bg-gold transition-[width]" style={{ width: `${progress}%` }} />
                    </div>
                  ) : (
                    <label data-testid={`onboarding-upload-${item.key}`}
                      className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blush-300 bg-white px-4 py-4 text-xs text-charcoal-muted transition-colors hover:border-gold/50 hover:bg-blush-50">
                      <UploadCloud className="h-4 w-4 text-gold" strokeWidth={1.5} /> Optionally attach PPD/TB results or chest X-ray report (PDF, JPG, PNG)
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={(e) => upload(item.key, e.target.files[0])} />
                    </label>
                  )}
                </div>
              )}

              {item.type !== "i9" && item.type !== "form" && renderActions(item)}
              {item.type === "form" && item.complete && renderActions(item)}
            </div>
          ))}
        </div>
      </main>

      <Dialog open={!!signItem} onOpenChange={() => { setSignItem(null); setSignName(""); }}>
        <DialogContent data-testid="sign-dialog" className="max-h-[85vh] overflow-y-auto rounded-3xl border-blush-200 bg-white sm:max-w-lg">
          {signItem && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-charcoal">{signItem.title}</DialogTitle>
                <DialogDescription className="text-sm text-charcoal-muted">Read carefully, then type your full legal name to sign electronically.</DialogDescription>
              </DialogHeader>
              <div className="rounded-2xl border border-blush-200 bg-blush-50/60 p-5 text-sm leading-relaxed text-charcoal-light" data-testid="sign-acknowledgment-text">
                {state.acknowledgment_texts[signItem.key]}
              </div>
              <div className="mt-4">
                <Field label="Electronic signature (full legal name)" htmlFor="sign-name" required>
                  <input id="sign-name" value={signName} onChange={(e) => setSignName(e.target.value)}
                    className="field-input font-serif !text-lg italic" data-testid="sign-signature-input" placeholder="Type your full legal name" />
                </Field>
              </div>
              <button onClick={sign} disabled={busy === signItem.key} data-testid="sign-submit-button" className="btn-pink mt-5 w-full disabled:opacity-60">
                {busy === signItem.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
                Sign & Acknowledge
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingPortal;
